import React, { useState, useCallback, useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";
import SubitemStatusIndicator from "./SubitemStatusIndicator";
import { SubitemsService } from "../services/SubitemsService";
import "./StatusAdvancementWarningModal.css";
import { useTrackModal } from "../hooks/useTrackModal";

// Stage colors configuration matching the tab colors from SubitemsTable
const STAGE_COLORS = {
  '01-Data Prep': { bg: '#f59e0b', text: '#ffffff' },      // Amber
  '02-Utilities': { bg: '#10b981', text: '#ffffff' },       // Emerald  
  '03-Discrepancy Check': { bg: '#ef4444', text: '#ffffff' }, // Red
  '04-Erosion/Pond': { bg: '#06b6d4', text: '#ffffff' },   // Cyan
  '05-Modeling': { bg: '#8b5cf6', text: '#ffffff' },       // Violet
  '06-Double Check': { bg: '#f97316', text: '#ffffff' },   // Orange
  '07-Corrections': { bg: '#ec4899', text: '#ffffff' },    // Pink
  '08-Review': { bg: '#6366f1', text: '#ffffff' },         // Indigo
  '09-Ready to Export': { bg: '#10b981', text: '#ffffff' }, // Emerald
  '10-Completed': { bg: '#6b7280', text: '#ffffff' }       // Gray
};

const StatusAdvancementWarningModal = ({
  isOpen,
  onClose,
  onOverride,
  incompleteSubitems = [],
  currentStatus,
  targetStatus,
  projectId,
  employees = [],
  onSubitemUpdate,
  onRevalidate,
}) => {
  useTrackModal("StatusAdvancementWarningModal", isOpen);
  const [localSubitems, setLocalSubitems] = useState(incompleteSubitems);
  const [updatingItems, setUpdatingItems] = useState(new Set());

  // Update local subitems when prop changes
  useEffect(() => {
    setLocalSubitems(incompleteSubitems);
  }, [incompleteSubitems]);

  // Handle status changes for subitems
  const handleStatusChange = useCallback(async (subitemId, newStatus) => {
    const updateKey = `${subitemId}-status`;
    if (updatingItems.has(updateKey)) return;


    // Add to updating set
    setUpdatingItems(prev => new Set(prev).add(updateKey));

    try {
      // Update in database
      await SubitemsService.updateSubitem(subitemId, { subitemStatus: newStatus });

      // Update local state immediately
      setLocalSubitems(prev => 
        prev.map(item => 
          item.id === subitemId ? { ...item, subitemStatus: newStatus } : item
        )
      );

      // Notify parent component
      if (onSubitemUpdate) {
        onSubitemUpdate(subitemId, 'subitemStatus', newStatus);
      }


      // Check if all items are now complete - if so, trigger re-validation
      const updatedItems = localSubitems.map(item => 
        item.id === subitemId ? { ...item, subitemStatus: newStatus } : item
      );
      
      const remainingIncomplete = updatedItems.filter(item => {
        const status = item.subitemStatus;
        return !status || status === null || status === 'Working on it' || status === 'Stuck';
      });

      if (remainingIncomplete.length === 0 && onRevalidate) {
        setTimeout(() => onRevalidate(), 500); // Small delay to ensure database is updated
      }

    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      // Remove from updating set
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(updateKey);
        return next;
      });
    }
  }, [localSubitems, updatingItems, onSubitemUpdate, onRevalidate]);

  if (!isOpen) return null;

  return (
    <div className="status-warning-modal-overlay" onClick={onClose}>
      <div
        className="status-warning-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="status-warning-modal-header critical">
          <div className="status-warning-modal-title">
            <AlertTriangle size={24} className="warning-icon" />
            <h2>Critical: Incomplete Stage Requirements</h2>
          </div>
          <button className="status-warning-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="status-warning-modal-body">
          <div className="warning-message">
            <p className="warning-text">
              <strong>WARNING:</strong> You are attempting to advance from{" "}
              <strong>{currentStatus}</strong> to{" "}
              <strong>{targetStatus}</strong> with {localSubitems.length}{" "}
              incomplete checklist item
              {localSubitems.length > 1 ? "s" : ""} from previous stages.
            </p>
            <p className="warning-subtext">
              All checklist items from previous stages should be completed
              (including marking items as N/A) before advancing to the next
              stage. Proceeding may result in missed requirements or incomplete
              deliverables.
            </p>
          </div>

          <div className="incomplete-subitems-table">
            <h3>Incomplete Checklist Items ({localSubitems.length}) - Mark as complete to continue:</h3>
            <div className="warning-subitems-table-container">
              <table className="warning-subitems-table">
                <thead>
                  <tr>
                    <th className="subitem-name-col">Name</th>
                    <th className="subitem-status-col">Status</th>
                    <th className="subitem-user-col">User</th>
                  </tr>
                </thead>
                <tbody>
                  {localSubitems.map((subitem) => {
                    const isStatusUpdating = updatingItems.has(`${subitem.id}-status`);
                    
                    return (
                      <tr key={subitem.id} className="warning-subitem-row">
                        <td className="subitem-name-cell">
                          {subitem.phase && (
                            <span 
                              className="warning-stage-badge"
                              style={{
                                backgroundColor: STAGE_COLORS[subitem.phase]?.bg || '#6b7280',
                                color: STAGE_COLORS[subitem.phase]?.text || '#ffffff'
                              }}
                            >
                              {subitem.phase.replace(/^\d+-/, '').toUpperCase()}
                            </span>
                          )}
                          <span className="warning-subitem-task-name">{subitem.name}</span>
                        </td>
                        <td className="subitem-status-cell">
                          <SubitemStatusIndicator
                            status={subitem.subitemStatus || ''}
                            onStatusChange={(newStatus) => handleStatusChange(subitem.id, newStatus)}
                            isUpdating={isStatusUpdating}
                          />
                        </td>
                        <td className="subitem-user-cell">
                          <span className="warning-user-display">
                            {subitem.employee?.name || subitem.user || 'Unassigned'}
                          </span>
                          {isStatusUpdating && <span className="warning-saving-indicator">💾</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="warning-question">
            <p>
              Are you absolutely sure you want to proceed without completing
              these checklist items?
            </p>
          </div>
        </div>

        <div className="status-warning-modal-footer">
          <button className="status-warning-override-btn" onClick={onOverride}>
            <AlertTriangle size={16} />
            Override & Advance Status
          </button>
          <button className="status-warning-primary-btn" onClick={onClose}>
            Cancel & Complete Items First
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusAdvancementWarningModal;
